import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import buddyService from '../services/buddyService';
import { authAPI } from '../services/api';
import { toast, ToastContainer } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUserFriends, 
  faArrowLeft, 
  faSearch,
  faLock
} from '@fortawesome/free-solid-svg-icons';
import 'react-toastify/dist/ReactToastify.css';
import FacilitatorCard from '../components/buddy/FacilitatorCard';
import RequestMatchModal from '../components/buddy/RequestMatchModal';
import MyRequestsSection from '../components/buddy/MyRequestsSection';
import CurrentMatchView from '../components/buddy/CurrentMatchView';
import PremiumUpgradeModal from '../components/buddy/PremiumUpgradeModal';

const BuddyPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [facilitators, setFacilitators] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [currentMatch, setCurrentMatch] = useState(null);
  const [selectedFacilitator, setSelectedFacilitator] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSpecialization, setFilterSpecialization] = useState('all');
  const [userSubscriptionTier, setUserSubscriptionTier] = useState('free');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Get current user data to check user type (free vs enterprise)
      try {
        const userResponse = await authAPI.getCurrentUser();
        // Check user_type instead of subscription_tier
        const userType = userResponse.data?.user_type || 'free';
        console.log('🔍 User type from API:', userType);
        console.log('🔍 Full user response:', userResponse.data);
        setUserSubscriptionTier(userType);
      } catch (error) {
        console.error('Error getting user data:', error);
      }

      // Check if user has a current match
      try {
        const matchResponse = await buddyService.getCurrentMatch();
        setCurrentMatch(matchResponse);
      } catch (error) {
        // No current match
        setCurrentMatch(null);
      }

      // Load facilitators
      const facilitatorsResponse = await buddyService.getFacilitators();
      setFacilitators(facilitatorsResponse.facilitators || []);

      // Load my requests
      const requestsResponse = await buddyService.getMyRequests();
      setMyRequests(requestsResponse.requests || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load buddy data');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestMatch = (facilitator) => {
    setSelectedFacilitator(facilitator);
    setShowRequestModal(true);
  };

  const handleBookSession = () => {
    // Check if user is free tier
    if (userSubscriptionTier === 'free') {
      setShowPremiumModal(true);
      return;
    }
    // Allow enterprise users to book
    // The actual booking logic would be in CurrentMatchView component
  };

  const handleSubmitRequest = async (requestData) => {
    try {
      await buddyService.sendMatchRequest({
        facilitator_id: selectedFacilitator.id,
        ...requestData
      });
      toast.success('Match request sent successfully!');
      setShowRequestModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send request');
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this request?')) {
      return;
    }

    try {
      await buddyService.cancelRequest(requestId);
      toast.success('Request cancelled');
      loadData();
    } catch (error) {
      toast.error('Failed to cancel request');
    }
  };

  // Get unique specializations for filter
  const allSpecializations = [...new Set(
    facilitators.flatMap(f => f.specializations || [])
  )];

  // Filter facilitators
  const filteredFacilitators = facilitators.filter(f => {
    const matchesSearch = f.user?.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         f.bio?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSpecialization = filterSpecialization === 'all' || 
                                  f.specializations?.includes(filterSpecialization);
    return matchesSearch && matchesSpecialization;
  });

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
            <FontAwesomeIcon icon={faUserFriends} style={{ color: '#FFB6C1' }} spin />
          </div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      color: '#ffffff',
      padding: '2rem'
    }}>
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />
      
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <button 
          onClick={() => navigate('/dashboard')} 
          style={{
            background: 'none',
            border: '1px solid #FFB6C1',
            color: '#FFB6C1',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '2rem'
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
        </button>

        <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '2.5rem', marginBottom: '0.5rem', color: '#FFB6C1' }}>
              <FontAwesomeIcon icon={faUserFriends} /> Buddy System
            </h1>
            <p style={{ color: '#999', fontSize: '1.1rem' }}>
              Connect with a trained facilitator for personalized support
            </p>
          </div>
          
        </div>

        {/* Current Match Section */}
        {currentMatch && (
          <CurrentMatchView 
            matchData={currentMatch} 
            onRefresh={loadData}
            onBookSession={handleBookSession}
            isPremium={userSubscriptionTier !== 'free'}
          />
        )}
        {currentMatch && console.log('🔍 Passing isPremium:', userSubscriptionTier !== 'free', 'userSubscriptionTier:', userSubscriptionTier)}

        {/* My Requests Section */}
        {myRequests.length > 0 && (
          <MyRequestsSection 
            requests={myRequests}
            onCancel={handleCancelRequest}
            onRefresh={loadData}
          />
        )}

        {/* Browse Facilitators (only if no current match) */}
        {!currentMatch && (
          <>
            <div style={{
              backgroundColor: 'rgba(26, 26, 26, 0.5)',
              border: '1px solid #333',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem'
            }}>
              <h2 style={{ color: '#FFB6C1', marginBottom: '1rem', fontSize: '1.5rem' }}>
                Browse Facilitators
              </h2>

              {/* Search and Filter */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                {/* Search */}
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon 
                    icon={faSearch} 
                    style={{
                      position: 'absolute',
                      left: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#999'
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Search facilitators..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem 0.75rem 2.5rem',
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                </div>

                {/* Specialization Filter */}
                <select
                  value={filterSpecialization}
                  onChange={(e) => setFilterSpecialization(e.target.value)}
                  style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  <option value="all">All Specializations</option>
                  {allSpecializations.map(spec => (
                    <option key={spec} value={spec}>{spec}</option>
                  ))}
                </select>
              </div>

              {/* Facilitator Grid */}
              {filteredFacilitators.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem',
                  color: '#999'
                }}>
                  <FontAwesomeIcon icon={faUserFriends} size="3x" style={{ marginBottom: '1rem' }} />
                  <p>No facilitators found matching your criteria</p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '1.5rem'
                }}>
                  {filteredFacilitators.map(facilitator => (
                    <FacilitatorCard
                      key={facilitator.id}
                      facilitator={facilitator}
                      onRequestMatch={handleRequestMatch}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Request Match Modal */}
      {showRequestModal && (
        <RequestMatchModal
          facilitator={selectedFacilitator}
          onClose={() => setShowRequestModal(false)}
          onSubmit={handleSubmitRequest}
        />
      )}

      {/* Premium Upgrade Modal */}
      {showPremiumModal && (
        <PremiumUpgradeModal
          onClose={() => setShowPremiumModal(false)}
        />
      )}
    </div>
  );
};

export default BuddyPage;
